import React from 'react';
import { classes, style } from 'typestyle';
import { percent, px } from 'csx';
import { flexRoot, horizontal, vertical, wrap } from 'csstips';
import { colorGunmetal, rebrand } from '../../../theme/color';
import { mediaMobileOnly } from '../../../theme/media';
import { TrackLine } from '../../../components/project-happy/molecules/TrackLine';
import { spacing } from '../../../theme/spacing';
import { fontstackDefault } from '../config';
import { TrackFeatureCard } from '../molecules/TrackFeatureCard';
import { getBpmHref, getGenreOrMoodHref, getTempoHref } from '../utilities/search';
import { TagButton } from '../atoms/button/TagButton';
import { ReleaseTile } from '../molecules/ReleaseTile';
import { VideoPlatform } from '../molecules/VideoPlatform';
import { mediaSmallTablet } from '../utilities/mediaQueries';
import { ClaimsManagedStatus } from '../molecules/ClaimsManagedStatus';
import { combinedPCLine } from '../utilities/copyright';
import { TrackSchemaWithUsage } from '../../../modules/page/checkout/model';
import { TrackPlaylistCarousel } from './TrackPlaylistCarousel';

interface TrackPageContentProps {
  track: TrackSchemaWithUsage;
}

const styles = {
  section: style({
    $nest: {
      '& + &': {
        marginTop: spacing.XLARGE,
      },
    },
  }),
  sectionHeading: style({
    margin: `0 0 ${spacing.DEFAULT}`,
    fontSize: px(32),
  }),
  trackInfo: style(
    vertical,
    {
      marginTop: spacing.DEFAULT,
      gap: spacing.DEFAULT,
    },
    mediaSmallTablet(horizontal)
  ),
  trackInfoColumn: style(
    mediaSmallTablet({
      width: `calc(50% - (${spacing.DEFAULT} / 2))`,
    })
  ),
  trackInfoSection: style({
    $nest: {
      '& + &': {
        marginTop: spacing.DEFAULT,
      },
    },
  }),
  trackInfoHeading: style({
    fontFamily: fontstackDefault,
    fontSize: px(16),
    fontWeight: 700,
    margin: `0 0 ${spacing.XSMALL}`,
  }),
  trackTags: style(flexRoot, wrap, {
    gap: spacing.XXSMALL,
  }),
  meta: style({
    color: rebrand.neutralOnLight[70].toString(),
    fontSize: px(12),
    whiteSpace: 'pre-line',
  }),
  platforms: style(flexRoot, wrap, {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    gap: spacing.XXSMALL,
  }),
  releases: style(horizontal, wrap, { gap: spacing.DEFAULT, width: percent(100) }),
  release: style(
    { width: `calc(25% - (${spacing.DEFAULT} * 3 / 4))` },
    mediaMobileOnly({ width: `calc(50% - (${spacing.DEFAULT}/ 2))` })
  ),
  tracks: style({
    margin: '16px 0',
    $nest: {
      '& h3': {
        margin: '30px 0',
      },
      '& h3 a': {
        color: colorGunmetal.toString(),
      },
    },
  }),
};

export const TrackPageContent: React.FC<TrackPageContentProps> = ({ track }) => {
  const listedRightsholders = track.rightsholders ? track.rightsholders.filter((label) => label.is_listed) : [];

  const genres = track.tags?.genre || [];
  const moods = track.tags?.mood || [];

  const isClaimsManaged = track.reporting_party.slug === 'umg';

  return (
    <>
      <h3>Track</h3>

      <TrackFeatureCard track={track} />

      <div className={classes(styles.section, styles.trackInfo)}>
        <div className={styles.trackInfoColumn}>
          {genres.length > 0 && (
            <div className={styles.trackInfoSection}>
              <h4 className={styles.trackInfoHeading}>Genres</h4>

              <div className={styles.trackTags}>
                {genres.map((genre) => (
                  <TagButton
                    data-test-genre-tag={genre.name}
                    key={genre.slug}
                    tagStyle="small"
                    href={getGenreOrMoodHref('genre', genre.slug)}
                    onClick={() => {
                      return null;
                    }}
                    text={genre.name}
                    aria-label={`Search for tracks by genre: ${genre.name}`}
                  />
                ))}
              </div>
            </div>
          )}

          {moods.length > 0 && (
            <div className={styles.trackInfoSection}>
              <h4 className={styles.trackInfoHeading}>Moods</h4>

              <div className={styles.trackTags}>
                {moods.map((mood) => (
                  <TagButton
                    data-test-mood-tag={mood.name}
                    key={mood.slug}
                    tagStyle="small"
                    href={getGenreOrMoodHref('mood', mood.slug)}
                    onClick={() => {
                      return null;
                    }}
                    text={mood.name}
                    aria-label={`Search for tracks by mood: ${mood.name}`}
                  />
                ))}
              </div>
            </div>
          )}

          {((track.tempo && track.tempo !== 'Unknown') || !!track.bpm) && (
            <div className={styles.trackInfoSection}>
              <h4 className={styles.trackInfoHeading}>Tempo</h4>

              <div className={styles.trackTags}>
                {!!track.bpm && (
                  <TagButton
                    data-test-tempo-tag={track.bpm}
                    tagStyle="small"
                    href={getBpmHref(track)}
                    onClick={() => {
                      return null;
                    }}
                    text={`${track.bpm} BPM`}
                    aria-label={`Search for tracks by BPM: ${track.bpm}`}
                  />
                )}
                {!!track.tempo && (
                  <TagButton
                    data-test-tempo-tag={track.tempo}
                    tagStyle="small"
                    href={getTempoHref(track)}
                    onClick={() => {
                      return null;
                    }}
                    text={track.tempo}
                    aria-label={`Search for tracks by tempo: ${track.tempo}`}
                  />
                )}
              </div>
            </div>
          )}
        </div>

        <div className={styles.trackInfoColumn}>
          {track.video_platforms && (
            <div className={styles.trackInfoSection}>
              <h4 className={styles.trackInfoHeading}>Target platforms</h4>

              <ul className={styles.platforms}>
                {Object.entries(track.video_platforms).map(([name, allowed]) => (
                  <VideoPlatform key={name} name={name} allowed={allowed} />
                ))}
              </ul>
            </div>
          )}

          <div className={styles.trackInfoSection}>
            <h4 className={styles.trackInfoHeading}>
              {isClaimsManaged ? 'Manually ' : 'Automatically '} cleared claims
            </h4>

            <ClaimsManagedStatus track={track} />
          </div>

          <div className={styles.trackInfoSection}>
            <div className={styles.meta}>{combinedPCLine(track)}</div>
          </div>
        </div>
      </div>

      {listedRightsholders.length > 0 && (
        <div className={styles.section}>
          <h3>Labels</h3>
          <div>
            {listedRightsholders.map((rightsholder, index) => (
              <TagButton
                key={rightsholder.identity}
                tagStyle="small"
                href={`/search?rightsholder=${rightsholder.name}`}
                onClick={() => {
                  return null;
                }}
                text={rightsholder.name}
              />
            ))}
          </div>
        </div>
      )}

      {track.identity && track.mixes.length > 0 && (
        <div className={styles.section}>
          <h3>Mixes</h3>

          {track.mixes.map((mix) => (
            <TrackLine key={mix.identity} track={mix} mix={true} />
          ))}
        </div>
      )}

      {track.releases && track.releases.length > 0 && (
        <div className={styles.section}>
          <h3>Released on</h3>

          <div className={styles.releases}>
            {track.releases.map((release) => (
              <ReleaseTile key={release.identity} className={styles.release} release={release} />
            ))}
          </div>
        </div>
      )}

      <TrackPlaylistCarousel track={track} />
    </>
  );
};
